import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewEncapsulation } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { buildProviders, KalDialogConfig, KalDialogService } from '@kalidea/kaligraphi';
import { Select, Store } from '@ngxs/store';
import gql from 'graphql-tag';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { filter, switchMap, tap } from 'rxjs/operators';
import { GraphqlService } from 'src/app/_core/services/graphql.service';
import { CustomFormControlAccess } from 'src/app/_core/tools/custom-form-control-access';
import {
    ConfirmDialogComponent,
    ConfirmDialogData
} from 'src/app/_shared/dialogs/confirm-dialog/confirm-dialog.component';
import {
    DocumentBankListDialogComponent,
    DocumentBankListDialogData
} from 'src/app/_shared/dialogs/document-bank-dialog/document-bank-list-dialog/document-bank-list-dialog.component';
import {
    WebAdministrationDocumentaryManagementInitRelatedListsAction
} from 'src/app/board/web-administration/stores/web-administration-documentary-management/web-administration-documentary-management-record/web-administration-documentary-management-record.action';
import {
    WebAdministrationDocumentaryManagementRecordState
} from 'src/app/board/web-administration/stores/web-administration-documentary-management/web-administration-documentary-management-record/web-administration-documentary-management-record.state';
import { arrayEmpty } from 'src/app/utils/array.utils';
import { FormValidator } from 'src/app/validators/form-validator';
import {
    ArrayOfPortailStatutDto,
    FichierCompletModel,
    MutationGetListePortailStatutGeneriqueArgs,
    PortailAlbumDto,
    PortailAlbumDtoInput,
    PortailStatutDto,
    StatusDtoEnum
} from 'src/graphql/generated-types';

const RECORD_LABEL_MAX_LENGTH = 20;
const RECORD_DESCRIPTION_MAX_LENGTH = 255;

@Component({
    selector: 'app-folder-bank-add',
    templateUrl: './folder-bank-add.component.html',
    styleUrls: ['./folder-bank-add.component.sass'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    providers: buildProviders(FolderBankAddComponent),
    standalone: false
})
export class FolderBankAddComponent extends CustomFormControlAccess implements OnInit {

    @Select(WebAdministrationDocumentaryManagementRecordState.statusList) statusList$: Observable<PortailStatutDto[]>;

    form: UntypedFormGroup;

    shouldDisplayDescription = false;

    shouldDisplayPostDateRange = false;

    fileName: string = null;

    statusList: PortailStatutDto[] = [];

    private _fileList$ = new BehaviorSubject([]);

    constructor(protected readonly cdr: ChangeDetectorRef,
                private readonly graphqlService: GraphqlService,
                private readonly dialogService: KalDialogService,
                private readonly route: ActivatedRoute,
                private readonly fb: UntypedFormBuilder,
                protected readonly store: Store) {
        super();
    }

    get fileList(): FichierCompletModel[] {
        return this._fileList$.getValue();
    }

    set fileList(value: FichierCompletModel[]) {
        this._fileList$.next(value);
    }

    get isFileListEmpty(): boolean {
        return arrayEmpty(this.fileList);
    }

    writeValue(value: PortailAlbumDto): void {
        super.writeValue(value);
    }

    openFileSelectionDialog(): void {
        let dialogConfig = new KalDialogConfig<DocumentBankListDialogData>({
            title: 'Ajouter un document',
            hasBackdrop: true,
            backdropClass: 'cdk-overlay-dark-backdrop',
            data: {
                shouldDisableSaveDocument: true,
                disabledFilters: {
                    categoryCode: true,
                    type: true
                }
            }
        });

        this.watch$ = this.dialogService.open(DocumentBankListDialogComponent, dialogConfig).afterClosed.pipe(
            filter(dialogResult => !!dialogResult?.accepted),
            switchMap(dialogResult => {
                const fetchedFileList = [...this.fileList];

                if (!dialogResult.selectedDocument.IdFile) { // fichier local
                    fetchedFileList.push(dialogResult.selectedDocument);

                    this.fileList = fetchedFileList;
                    this.cdr.markForCheck();
                } else { // fichier sélectionné depuis la liste
                    const isFileExists = !!this.fileList.find(f => f.IdFile === dialogResult.selectedDocument.IdFile);

                    if (isFileExists) {
                        dialogConfig = new KalDialogConfig<ConfirmDialogData>({
                            title: 'Document déjà présent',
                            hasBackdrop: true,
                            backdropClass: 'cdk-overlay-dark-backdrop',
                            data: {
                                body: 'Le document sélectionné dans la bibliothèque est déjà présent dans ce dossier.',
                                submitLabel: 'Fermer'
                            }
                        });

                        return this.dialogService.open(ConfirmDialogComponent, dialogConfig).afterClosed.pipe(switchMap(() => EMPTY));
                    }

                    fetchedFileList.push(dialogResult.selectedDocument);

                    this.fileList = fetchedFileList;
                    this.notifyUpdate(this.normalize());
                    this.cdr.markForCheck();
                }

                return of(null);
            })
        );
    }

    validate(c: AbstractControl): Observable<ValidationErrors> {
        return of(this.form.valid ? null : {documentlocalfile: true});
    }

    toggleDescription(): void {
        this.shouldDisplayDescription = !this.shouldDisplayDescription;
    }

    togglePostDateRange(): void {
        this.shouldDisplayPostDateRange = !this.shouldDisplayPostDateRange;
    }

    private getStatusList$(): Observable<ArrayOfPortailStatutDto> {
        return this.graphqlService.mutateOne<'GetListePortailStatutGenerique', MutationGetListePortailStatutGeneriqueArgs>({
            mutation: gql`
                mutation GetListePortailStatutGenerique(
                    $contexte: ContextInfosInput
                    $criteres: CriteresPortailStatutDTOInput
                ) {
                    GetListePortailStatutGenerique(
                        contexte: $contexte
                        criteres: $criteres
                    ) {
                        PortailStatutDTO {
                            Code
                            Id
                            IsActif
                            Libelle
                        }
                    }
                }
            `
        }).pipe(
            tap(GetListePortailStatutGenerique => this.statusList = GetListePortailStatutGenerique?.PortailStatutDTO || [])
        );
    }

    private normalize(): PortailAlbumDtoInput {
        const {
            Libelle,
            IdStatut,
            Description,
            DateDossier
        } = this.form.value;

        return {
            Status: StatusDtoEnum.Inserted,
            Id: 0,
            Libelle,
            Description,
            Statut: this.statusList.find(s => s.Id === IdStatut),
            DateDebut: DateDossier.startDate,
            DateFin: DateDossier.endDate,
            ListeDocumentsToSave: {
                FichierCompletModel: this.fileList
            }
        };
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            Libelle: [null, [FormValidator.isNotNull, Validators.maxLength(RECORD_LABEL_MAX_LENGTH)]],
            IdStatut: [null, [FormValidator.isNotNull]],
            Description: [null, [Validators.maxLength(RECORD_DESCRIPTION_MAX_LENGTH)]],
            DateDossier: [{
                startDate: null,
                endDate: null
            }]
        });
        this.watch$ = this.getStatusList$();

        this.watch$ = this.store.dispatch(new WebAdministrationDocumentaryManagementInitRelatedListsAction());

        this.watch$ = this.form.valueChanges.pipe(
            tap(() => {
                this.notifyUpdate(this.normalize());
                this.cdr.markForCheck();
            })
        );
    }

}
